import React from 'react'
import PropTypes from 'prop-types'
import {Box, Text, useBreakpointValue, VisuallyHidden} from '@chakra-ui/react'
import DynamicImage from '@app/components/dynamic-image'
import usePDCResponsiveElement from '@app/hooks/use-pdc-responsive-element.js'
import {DYNAMIC_IMAGE_WIDTHS} from '@app/constants.js'
import {FormattedMessage} from 'react-intl'

/**
 * PDCStyleGuideTile is a React functional component that renders a styled tile
 * element, typically used for displaying a style guide entry in a page designer
 * context. It adapts its content based on the provided props and the current
 * responsive breakpoint.
 *
 * @param {Object} props - The properties object.
 * @param {string} props.title - The title of the style guide tile, displayed as
 *                               the main text.
 * @param {string} props.desc - A description text that is conditionally rendered
 *                              if `isPDP` is true.
 * @param {string} props.modelId - The unique identifier for the model associated
 *                                 with this tile.
 * @param {Object} props.smallImage - The image object to be used for small
 *                                    breakpoints.
 * @param {Object} props.mediumImage - The image object to be used for medium
 *                                     breakpoints.
 * @param {Object} props.largeImage - The image object to be used for large
 *                                    breakpoints.
 * @param {Object} props.xlargeImage - The image object to be used for extra-large
 *                                     breakpoints.
 * @param {Object} props.xxlargeImage - The image object to be used for
 *                                      double-extra-large breakpoints.
 * @param {string} props.imageTitle - The title attribute for the image, used for
 *                                    accessibility and tooltips.
 * @param {string} props.imageAlt - The alt attribute for the image, used for
 *                                  accessibility.
 * @param {boolean} props.isPDP - A flag indicating if the component is rendered
 *                                in a Product Detail Page context, affecting the
 *                                rendering of the description.
 * @param {string} props.selectedModelId - The currently selected model ID, used
 *                                         to determine the background color of
 *                                         the tile.
 *
 * @returns {JSX.Element} A styled tile component that includes an image and
 *                        optional text content, adapting to responsive breakpoints.
 */
export const PDCStyleGuideTile = ({
    title,
    desc,
    modelId,
    smallImage,
    mediumImage,
    largeImage,
    xlargeImage,
    xxlargeImage,
    imageTitle,
    imageAlt,
    isPDP,
    selectedModelId,
    nbSlides,
    custom
}) => {
    // Get current image based on breakpoint
    const currentImage = useBreakpointValue(
        usePDCResponsiveElement({
            smallEl: custom?.smallImage?.disBaseLink || smallImage,
            mediumEl: custom?.mediumImage?.disBaseLink || mediumImage,
            largeEl: custom?.largeImage?.disBaseLink || largeImage,
            xlargeEl: custom?.xlargeImage?.disBaseLink || xlargeImage,
            xxlargeEl: custom?.xxlargeImage?.disBaseLink || xxlargeImage
        })
    )

    let imageWidths
    switch (nbSlides) {
        case 1:
            imageWidths = DYNAMIC_IMAGE_WIDTHS.full
            break
        case 2:
            imageWidths = DYNAMIC_IMAGE_WIDTHS.halfandfullonsmalldevice
            break
        case 3:
            imageWidths = DYNAMIC_IMAGE_WIDTHS.thirdandfullonsmalldevice
            break
        case 4:
            imageWidths = DYNAMIC_IMAGE_WIDTHS.quarterandfullonsmalldevice
            break
        default:
            imageWidths = DYNAMIC_IMAGE_WIDTHS.halfandfullonsmalldevice
            break
    }

    return (
        <Box width={'100%'}>
            <Box
                border={'1px solid'}
                borderColor={'borderGray'}
                width={'100%'}
                height={'auto'}
                bg={selectedModelId === modelId ? 'navyBlue' : 'whiteSmoke'}
            >
                {(currentImage || currentImage?.url) && (
                    <DynamicImage
                        src={`${
                            typeof currentImage === 'string' ? currentImage : currentImage?.url
                        }[?sw={width}]`}
                        widths={imageWidths}
                        imageProps={{
                            width: '100%',
                            loading: 'auto',
                            fetchpriority: 'high',
                            alt: imageAlt === 'empty' ? '' : imageAlt || title,
                            title: imageTitle === 'empty' ? '' : imageTitle || title
                        }}
                    />
                )}
                {selectedModelId === modelId && (
                    <VisuallyHidden>
                        <FormattedMessage
                            defaultMessage="Selected fit"
                            id="product_details_carousel.text.selected_fit"
                        ></FormattedMessage>
                    </VisuallyHidden>
                )}
            </Box>

            <Text
                as={'h3'}
                variant="descriptor"
                mt={7}
                dangerouslySetInnerHTML={{
                    __html: title
                }}
                bg="whiteSmoke"
                p={3}
                display={'inline-block'}
                color="darkGray"
            />

            {desc && isPDP && (
                <Text variant="bodyBase2" color={'darkGray'} mt={6}>
                    {desc}
                </Text>
            )}
        </Box>
    )
}

PDCStyleGuideTile.propTypes = {
    title: PropTypes.string.isRequired,
    desc: PropTypes.string,
    modelId: PropTypes.string,
    smallImage: PropTypes.object.isRequired,
    mediumImage: PropTypes.object,
    largeImage: PropTypes.object,
    xlargeImage: PropTypes.object,
    xxlargeImage: PropTypes.object,
    imageTitle: PropTypes.string,
    imageAlt: PropTypes.string,
    isPDP: PropTypes.bool,
    selectedModelId: PropTypes.string,
    nbSlides: PropTypes.number,
    custom: PropTypes.object
}
